import { useMemo, useState, useEffect } from 'react';
import { jwtDecode } from "jwt-decode";
import * as Constants from "../../constants";
import B2BApiService from "../../B2bApiService";
import Logout from '../../components/layoutComponents/navigation/Logout';
import PageLayout from "../../components/layoutComponents/PageLayout";
import InputError from "../../components/formComponents/InputError";
import apexLogo from "../../assets/images/logos/apex_analytics_green_logo.svg";
import checkGreen from '../../assets/images/icons/checkGreen.svg';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { TextField } from '@mui/material';
import AuthPageChrome from '../../components/layoutComponents/authComponents/AuthPageChrome';
import { t } from '../../content';

/**
 * TODO:
 * - IMPORTANT: Move company_enforce_mfa, mfa_activated, mfa_verified away from localStorage to avoid manual adjustment from users/hackers
 * - Allow users to manually activate, deactivate MFA (unless enforced by company)
 * - Replace hardcoded text with Contentful dynamic content (with translation to German, later)
 * - Add loading spinner when activating MFA
 * - Add loading spinner when verifying MFA code
 */
function MultiFactorAuthentication() {

    const authenticationData = JSON.parse(localStorage.getItem(Constants.MAZHR_JWT_KEY)) || null;

    const [step, setStep] = useState(1); // Tracks the current step in the MFA process
    const [mfaQRCode, setMfaQRcode] = useState(null);
    const [mfaSecretKey, setMfaSecretKey] = useState(null);
    const [activationError, setActivationError] = useState(null);
    const [isOnMFASetup, setIsOnMFASetup] = useState(false); // Tracks if the user is on the MFA setup process
    const [authCode, setAuthCode] = useState('');
    const [verificationError, setVerificationError] = useState(null);
    const [countdown, setCountdown] = useState(3);  // After vertification succeeded, countdown from 3 seconds before refirecting to dashboard 

    const b2bServiceObj = useMemo(() => {
        return new B2BApiService();
    }, []);

    useEffect(() => {
        // Unless user is in the MFA setup process, check if MFA is already activated but not verified
        if (!isOnMFASetup) {
            // For integrity check, if decoded JWT is available, use the values from JWT
            if (authenticationData?.access_token) {
                const decodedJWT = jwtDecode(authenticationData?.access_token?.toString());
                const mfa_activated = decodedJWT?.mfa_activated;
                const mfa_verified = decodedJWT?.mfa_verified;
                if (mfa_activated && !mfa_verified) {
                    setStep(3); // Skip to step 3 if MFA is already activated but not yet verified
                }
            // If the JWT is not available, check the localStorage values
            } else if (authenticationData?.mfa_activated && !authenticationData?.mfa_verified) {
                setStep(3); // Skip to step 3 if MFA is already activated but not yet verified
            }
        }
    }, [isOnMFASetup, authenticationData, step]);

    useEffect(() => {
        // When the screen is on step 4, start the countdown and redirect to the dashboard when it reaches 0
        if (step === 4) {
            if (countdown > 0) {
                const timer = setInterval(() => {
                    setCountdown(prevCountdown => prevCountdown - 1);
                }, 1000);
                return () => clearInterval(timer);
            } else {
                window.location.href = '/dashboard';
            }
        }
    }, [step, countdown]);

    const activateMFA = async () => {
        try {
            const response = await b2bServiceObj.ActivateUserMFA();
        
            if (response.httpStatus !== 200 || !response.json) {
                throw new Error('Failed to activate MFA');
            }
        
            const {
                qr_code = null,
                mfa_secret = null,
                access_token,
                refresh_token = null,
            } = response.json;
        
            setMfaQRcode(qr_code);
            setMfaSecretKey(mfa_secret);
            setStep(2); // Move to setup step
            setIsOnMFASetup(true); // Prevent auto-skipping to verification
        
            if (authenticationData && access_token) {
                localStorage.setItem(Constants.MAZHR_JWT_KEY, JSON.stringify({
                    ...authenticationData,
                    access_token,
                    refresh_token,
                    mfa_activated: true,
                }));
            }
        
            setActivationError(null);
        } catch (error) {
            console.error("MFA activation error:", error);
            setActivationError(
                t('auth.mfa.errors.activateUser')
            );
        }
    };
      

    const completeSetup = () => {
        setIsOnMFASetup(false);
        setStep(3);
    };

    const handleVerificationChange = (e) => {
        const value = e.target.value;
        setAuthCode(value);
    
        // Remove error if user starts typing
        if (value.trim() === '') {
          setVerificationError(t('validation.codeEmpty'));
        } else {
          setVerificationError(null);
        }
    };

    // Handle Enter key press when users verify the MFA code
    const handleVerificationKeyDown = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();  // Prevent form submission or default behavior
          verifyAuthCode();
        }
    };

    const verifyAuthCode = async () => {
        if (!authCode?.trim()) {
          setVerificationError(t('validation.codeEmpty'));
          return;
        }
      
        setVerificationError(null);
        const data = { mfa_code: authCode };
      
        try {
            const response = await b2bServiceObj.VerifyMFA(data);
        
            if (response.httpStatus !== 200 || !response.json) {
                throw new Error('Verification failed. Please try again.');
            }
            // If successful, update the JWT with the new authentication data
            const { access_token, refresh_token } = response.json;
            if (authenticationData && access_token && refresh_token) {
                localStorage.setItem(Constants.MAZHR_JWT_KEY, JSON.stringify({ 
                    ...authenticationData, 
                    access_token,
                    refresh_token,
                    mfa_verified: true }));
            } 
            setStep(4); // Success step
        } catch (error) {
            console.error("Error verifying MFA code:", error);
            setVerificationError(t('validation.incorrectCode'));
        } finally {
            setAuthCode('');
        }
    };

    return (   
        <PageLayout>
            <div 
                className="center-content" 
                style={{
                    minHeight: '100vh',
                    height: 'fit-content',
                    justifyContent: 'unset',
                    textAlign: 'center',
                }}
            >               
                <div className="mar-25 top">
                    <div className='mar-25 bottom auth-mfa-chrome'>
                        <AuthPageChrome logoSrc={apexLogo} />
                    </div>
                    {step === 1 && (
                        <>
                        <h2 className="title semi-bold l mar-15 bottom">{t('auth.mfa.titleHyphen')}</h2>
                        <p>{t('auth.mfa.orgRequires')}<br/>
                            {t('auth.mfa.clickToActivate')}
                        </p>
                        <button className="mazhrBtn default-green mar-20 top full-width" onClick={activateMFA}>
                            {t('auth.mfa.activateCta')}
                        </button>
                        {activationError && <InputError errorMessage={activationError} />}
                        <Logout>
                            <button className="mazhrBtn default-pink mar-20 top full-width" 
                                onClick={() => window.location.href = `mailto:security@apexteam.ai`} >
                                {t('auth.mfa.contactSupport')}
                            </button>
                        </Logout>
                        </>
                    )}

                    {step === 2 && (
                        <>
                        <h2>{t('auth.mfa.installationTitle')}</h2>
                        <p><span style={{color: 'red', fontWeight: 'bold'}}>
                            {t('auth.mfa.doNotClose')}</span><br/>
                            {t('auth.mfa.contactIfIssue')}
                        </p>
                        <p>{t('auth.mfa.installRecommend')}<br/>
                        <b>
                            {t('auth.mfa.googleAuthenticatorFor')} <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener noreferrer"> {t('auth.mfa.android')} </a> {t('auth.mfa.or')} 
                            <a href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank" rel="noopener noreferrer"> {t('auth.mfa.ios')}</a>
                        <br/>
                            {t('auth.mfa.microsoftAuthenticatorFor')} <a href="https://play.google.com/store/apps/details?id=com.azure.authenticator" target="_blank" rel="noopener noreferrer"> {t('auth.mfa.android')}</a> {t('auth.mfa.or')} 
                            <a href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458" target="_blank" rel="noopener noreferrer"> {t('auth.mfa.ios')}</a>
                        <br/>
                            {t('auth.mfa.authyAuthenticatorFor')} <a href="https://play.google.com/store/apps/details?id=com.authy.authy" target="_blank" rel="noopener noreferrer">{t('auth.mfa.android')}</a> {t('auth.mfa.or')} 
                            <a href="https://apps.apple.com/us/app/authy/id494168017" target="_blank" rel="noopener noreferrer"> {t('auth.mfa.ios')}</a>
                        </b></p>
                        <p>
                            {t('auth.mfa.openApp')}<br/>
                            {t('auth.mfa.holdPhone')}<br/>
                            <strong>{t('auth.mfa.doNotUseCameraPrefix')}</strong> {t('auth.mfa.doNotUseCameraRest')} <br/>
                            {t('auth.mfa.useAuthenticatorPrefix')} <strong>{t('auth.mfa.authenticatorApp')}</strong> {t('auth.mfa.useAuthenticatorRest')}
                        </p>
                        <p style={{color: 'red', fontWeight: 'bold'}}>
                            {t('auth.mfa.willNotShowAgain')}<br/>
                            {t('auth.mfa.saveQrLater')}
                        </p>

                        {mfaQRCode ?
                            <img src={`data:image/png;base64,${mfaQRCode}`} alt={t('auth.mfa.qrCodeAlt')} height={400}/> : 
                            <InputError errorMessage={t('auth.mfa.qrError')} />
                        }

                        {mfaSecretKey && (
                            <p>{t('auth.mfa.enterSetupKey')} <strong>{mfaSecretKey.match(/.{1,4}/g)?.join(' ') || mfaSecretKey}</strong></p>
                        )}

                        <button className="mazhrBtn default-green mar-20 top full-width" onClick={completeSetup}>{t('auth.mfa.doneSetup')}</button>
                        </>
                    )}

                    {step === 3 && (
                        <>
                        <h2>{t('auth.mfa.verificationTitle')}</h2>
                        <p>
                            {t('auth.mfa.accountProtected')}<br/>
                            {t('auth.mfa.enterCodeFromApp')}<br/>
                            {t('auth.mfa.contactIfIssue')}
                        </p>
                        <TextField
                            fullWidth
                            placeholder={t('common.placeholders.enterCode')}
                            label={t('auth.mfa.authCode')} 
                            variant="standard"
                            name="authCode"
                            type="authCode"
                            autoComplete="on"
                            value={authCode}
                            inputProps={{style: { textAlign: 'center' }}}
                            onKeyDown={handleVerificationKeyDown}
                            onChange={handleVerificationChange}
                        />
                        <button className="mazhrBtn default-green mar-20 top full-width" onClick={verifyAuthCode}>{t('common.actions.verify')}</button>
                        {verificationError && <InputError errorMessage={verificationError} />}
                        <Logout>
                            <button className="mazhrBtn default-pink mar-20 top full-width" 
                                onClick={() => window.location.href = `mailto:security@apexteam.ai`} >
                                {t('auth.mfa.contactSupport')}
                            </button>
                        </Logout>
                        </>
                    )}

                    {step === 4 && (
                        <>
                        <div>
                            {CheckCircleOutlinedIcon 
                                ? <CheckCircleOutlinedIcon style={{ color: 'green', fontSize: 100 }} /> 
                                : <img src={checkGreen || ''} height={60} alt="verification_success" />
                            }
                        </div>
                        <h2>{t('auth.mfa.succeeded')}</h2>
                        <p>{t('auth.mfa.redirecting', { seconds: countdown })}</p>
                        </>
                    )}
                </div>
            </div>
        </PageLayout>
    );
}

export default MultiFactorAuthentication;


